import { EnvConfig } from '../config/EnvConfig'
import { getAuthKambiUser } from '../adapters/user'
import storageService from './storageService'
import { isMobileApp } from '../utils/mobileAppFunctionality'
import { kambiAuthApi } from '../adapters/base'
import { KambiLocaleSettings } from '../utils/constants'

const KAMBI_LOGIN_ENDPOINT = (offering) =>
  `/player/api/v2019/${offering}/punter/login.json?`

class KambiAuthService {
  constructor() {
    this.sessionToken = null
    this.sessionExpiry = null
    this.SESSION_DURATION = 30 * 60 * 1000
  }

  _hasValidSession(forceRefresh = false) {
    return (
      !forceRefresh &&
      this.sessionToken &&
      this.sessionExpiry &&
      Date.now() < this.sessionExpiry
    )
  }

  async getSessionToken(forceRefresh = false) {
    if (this._hasValidSession(forceRefresh)) {
      return { ok: true, data: this.sessionToken }
    }

    try {
      const user = storageService.getUser()
      if (!user) {
        return { ok: false, error: 'User not logged in' }
      }

      const offering = EnvConfig.GATSBY_KAMBI_OFFERING

      const authResponse = await getAuthKambiUser()
      if (!authResponse.ok) {
        return { ok: false, error: 'Failed to authenticate with Kambi' }
      }

      const ticket = authResponse.data.token
      const channel = isMobileApp() ? 'MOBILE' : 'WEB'
      const currentLocale = EnvConfig.GATSBY_INTL_LOCALE
      const market = KambiLocaleSettings[currentLocale]?.market || 'BR'

      const loginResponse = await kambiAuthApi.post(
        KAMBI_LOGIN_ENDPOINT(offering),
        {
          punterId: user.id,
          ticket,
          channel,
          market,
        }
      )

      this.sessionToken = loginResponse.data.token
      this.sessionExpiry = Date.now() + this.SESSION_DURATION

      return { ok: true, data: this.sessionToken }
    } catch (error) {
      return {
        ok: false,
        error:
          error?.response?.data ||
          error?.message ||
          'Failed to get session token',
      }
    }
  }

  async makeAuthenticatedRequest(endpoint, options = {}) {
    const sessionResponse = await this.getSessionToken()
    if (!sessionResponse.ok) {
      return sessionResponse
    }

    try {
      const response = await kambiAuthApi.request({
        url: endpoint,
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${sessionResponse.data}`,
        },
      })

      return { ok: true, data: response.data }
    } catch (error) {
      if (error?.response?.status === 401) {
        const refreshResponse = await this.getSessionToken(true)
        if (!refreshResponse.ok) {
          return refreshResponse
        }

        try {
          const retryResponse = await kambiAuthApi.request({
            url: endpoint,
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${refreshResponse.data}`,
            },
          })

          return { ok: true, data: retryResponse.data }
        } catch (retryError) {
          return {
            ok: false,
            error:
              retryError?.response?.data ||
              retryError?.message ||
              'Request failed after token refresh',
          }
        }
      }

      return {
        ok: false,
        error: error?.response?.data || error?.message || 'Request failed',
      }
    }
  }
}

export const kambiAuthService = new KambiAuthService()
